import { aiMimeTypes } from '@aline/common';
import { remark } from 'remark';
import { visit } from 'unist-util-visit';

import { parseJsonlToRedlineList } from './redlineCodeParser';
import type { ChatMessageSuggestionStatus, SuggestionStatus } from '../../chat-types';
import { buildRedlineStatusEntries } from '../elements/CodeElement/CodeRedlineListElement/redline-state-helpers';
import type { RedlineGroupStatus } from '../elements/CodeElement/CodeRedlineListElement/redline-types';

/**
 * Collects redline status entries from every `jsonl-redline-edit` code block
 * in a message's markdown.
 *
 * A message can contain multiple redline blocks (the model's reasoning can
 * close and reopen the fence), so entries are accumulated across all blocks
 * into one array. The caller must write them to the reactive variable in a
 * single set — setting once per block would let the last block's entries
 * replace the earlier blocks' statuses.
 */
export function collectRedlineStatuses(
  completionText: string,
  options: {
    documentInViewId: string | null;
    suggestionStates: Record<string, ChatMessageSuggestionStatus> | undefined;
  }
): { hasRedlineBlock: boolean; statuses: RedlineGroupStatus[] } {
  const { documentInViewId, suggestionStates } = options;

  /**
   * Build the server-status lookup (groupId -> status) from suggestionStates.
   * suggestionStates is keyed by groupId, and both edit actions and inserts
   * carry a groupId, so we convert the whole record rather than walking a
   * single redline kind (which is what dropped insert statuses before).
   */
  const serverStatuses: Record<string, SuggestionStatus> = {};
  for (const [groupId, state] of Object.entries(suggestionStates ?? {})) {
    if (state?.status) {
      serverStatuses[groupId] = state.status;
    }
  }

  let hasRedlineBlock = false;
  const statuses: RedlineGroupStatus[] = [];

  visit(remark().parse(completionText), 'code', node => {
    if (node.lang !== aiMimeTypes.jsonlRedlineEdit) return;
    hasRedlineBlock = true;

    const parsedItems = parseJsonlToRedlineList(node.value);

    // Calculate isDocumentInView per-item, not globally.
    statuses.push(
      ...parsedItems.flatMap(item =>
        buildRedlineStatusEntries(item, {
          isDocumentInView: Boolean(documentInViewId && item.documentId === documentInViewId),
          serverStatuses,
        })
      )
    );
  });

  return { hasRedlineBlock, statuses };
}
